// apps/admin/src/components/FastNavigationBar.tsx
import { Link } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import type { RoleType, UserScopes } from '@magnetar/admin-api-interfaces'
import { useMe } from '@/hooks/useMe'
import { useViewMode, ViewMode } from '@/stores/viewMode'
import { useClinicById } from '@/hooks/useClinicById'
import { useAdSupplierById } from '@/hooks/useAdSupplierById'
import { useClinicGroup } from '@/hooks/useClinicGroups'
import { useAdSupplierGroup } from '@/hooks/useAdSupplierGroups'

type Variant = 'desktop' | 'mobile'
type Ceiling = 'admin' | 'provider' | 'group' | 'leaf'
type ChainScope = 'admin' | 'provider' | 'clinic_group' | 'ad_supplier_group'

interface ChainItem {
  scope: ChainScope
  label: string
  href: string
}

interface FastNavigationBarProps {
  variant?: Variant
  onNavigate?: () => void
}

function getUserCeiling(
  roleType: RoleType | undefined,
  scopes: UserScopes | undefined,
): Ceiling {
  if (roleType === 'sys_admin') return 'admin'
  if (scopes?.provider_id) return 'provider'
  if (
    (scopes?.clinic_group_ids?.length ?? 0) > 0 ||
    (scopes?.ad_supplier_group_ids?.length ?? 0) > 0
  ) {
    return 'group'
  }
  return 'leaf'
}

// 父鏈順序：天花板 (admin) → ... → 直接父層。渲染時最遠祖先在最左，最近父層在最右。
const CEILING_RANK: Record<Ceiling, number> = { leaf: 0, group: 1, provider: 2, admin: 3 }
const SCOPE_RANK: Record<ChainScope, number> = {
  ad_supplier_group: 1,
  clinic_group: 1,
  provider: 2,
  admin: 3,
}

function filterByCeiling(chain: ChainItem[], ceiling: Ceiling): ChainItem[] {
  const max = CEILING_RANK[ceiling]
  return chain.filter((item) => SCOPE_RANK[item.scope] <= max)
}

function useAncestorChain(mode: ViewMode, id: number | null): ChainItem[] {
  // 一律呼叫所有 hooks（React rules-of-hooks）；真正用到再從結果挑值
  const clinic = useClinicById(mode === 'clinic' && id ? String(id) : undefined).clinic
  const adSupplier = useAdSupplierById(mode === 'ad' && id ? String(id) : undefined).adSupplier
  const clinicGroup = useClinicGroup(mode === 'clinic_group' ? id : null).clinicGroup
  const adSupplierGroup = useAdSupplierGroup(mode === 'ad_supplier_group' ? id : null)
    .adSupplierGroup

  // 父鏈以「最近父層在最後 push」的順序組裝；渲染時 reverse 顯示成 [admin, ..., 直接父]
  const chain: ChainItem[] = []
  const adminItem: ChainItem = { scope: 'admin', label: '← Admin Dashboard', href: '/admin' }

  if (mode === 'provider') {
    chain.push(adminItem)
    return chain
  }

  if (mode === 'clinic_group') {
    // admin 一律先放，provider 等資料到再放（避免 provider_id 為 null 或 fetch 未完時整條鏈消失）
    if (clinicGroup?.provider_id) {
      chain.push({
        scope: 'provider',
        label: '← Provider Dashboard',
        href: `/provider/${clinicGroup.provider_id}`,
      })
    }
    chain.push(adminItem)
    return chain
  }

  if (mode === 'ad_supplier_group') {
    if (adSupplierGroup?.provider_id) {
      chain.push({
        scope: 'provider',
        label: '← Provider Dashboard',
        href: `/provider/${adSupplierGroup.provider_id}`,
      })
    }
    chain.push(adminItem)
    return chain
  }

  if (mode === 'clinic' && clinic) {
    if (clinic.clinic_group_id) {
      chain.push({
        scope: 'clinic_group',
        label: '← ClinicGroup Dashboard',
        href: `/clinic-group/${clinic.clinic_group_id}`,
      })
    }
    if (clinic.provider_id) {
      chain.push({
        scope: 'provider',
        label: '← Provider Dashboard',
        href: `/provider/${clinic.provider_id}`,
      })
    }
    chain.push(adminItem)
    return chain
  }

  if (mode === 'ad' && adSupplier) {
    if (adSupplier.ad_supplier_group_id) {
      chain.push({
        scope: 'ad_supplier_group',
        label: '← AdSupplierGroup Dashboard',
        href: `/ad-supplier-group/${adSupplier.ad_supplier_group_id}`,
      })
    }
    if (adSupplier.provider_id) {
      chain.push({
        scope: 'provider',
        label: '← Provider Dashboard',
        href: `/provider/${adSupplier.provider_id}`,
      })
    }
    chain.push(adminItem)
    return chain
  }

  return chain
}

export default function FastNavigationBar(_props: FastNavigationBarProps) {
  // 所有 hook 都要先呼叫，不能放在 early return 之後（Rules of Hooks）
  const { mode, id } = useViewMode()
  const { user } = useMe()
  const fullChain = useAncestorChain(mode, id)

  if (mode === 'admin' || mode === 'default') return null

  const ceiling = getUserCeiling(user?.role_type, user?.scopes)
  if (ceiling === 'leaf') return null

  const visible = filterByCeiling(fullChain, ceiling)
  if (visible.length === 0) return null

  const variant = _props.variant ?? 'desktop'
  // 渲染順序：最遠祖先在最左／最上 → 最近父層在最右／最下
  // chain 內目前是「近 → 遠」push，所以 reverse 後才是顯示順序
  const ordered = [...visible].reverse()

  if (variant === 'desktop') {
    return (
      <nav
        aria-label="快速回上層"
        className="hidden md:flex flex-wrap fixed top-4 left-[17rem] right-[5rem] z-30 items-center gap-2"
      >
        {ordered.map((item) => (
          <Link
            key={item.scope}
            to={item.href}
            onClick={() => _props.onNavigate?.()}
            className="inline-flex items-center gap-1 rounded-full bg-white/90 backdrop-blur-sm px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm border border-gray-200 hover:bg-white hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>{item.label.replace(/^← /, '')}</span>
          </Link>
        ))}
      </nav>
    )
  }

  // variant === 'mobile'
  return (
    <nav
      aria-label="快速回上層"
      className="md:hidden flex flex-col gap-1 px-4 py-3 border-b border-gray-200"
    >
      {ordered.map((item) => (
        <Link
          key={item.scope}
          to={item.href}
          onClick={() => _props.onNavigate?.()}
          className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>{item.label.replace(/^← /, '')}</span>
        </Link>
      ))}
    </nav>
  )
}
